import { Ban, CircleCheck } from 'lucide-react';
import React from 'react';
import { ProductState } from '@api/WalletClient';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@core/utils/cn';
import { Tooltip } from '../../components/tooltip';

interface StateSummaryProps {
    internalState?: ProductState;
    externalState?: ProductState;
    shopState?: ProductState;
    loading: boolean;
    enableLabel?: string;
    onToggleState(state: ProductState): Promise<void>;
}

const StateBadge = ({
    state,
    empty
}: {
    state?: ProductState;
    empty: string;
}) => (
    <Badge variant={state === ProductState.PRODUCTION ? 'success' : 'muted'}>
        {state || empty}
    </Badge>
);

const StatePanel = ({
    label,
    tooltip,
    highlighted,
    children
}: {
    label: string;
    tooltip: string;
    highlighted?: boolean;
    children: React.ReactNode;
}) => (
    <div
        className={cn(
            'flex-1 rounded-md border p-3',
            highlighted && 'border-2 border-primary bg-accent/50'
        )}
    >
        <div className="mb-1.5 flex items-center gap-1">
            <span className="text-xs font-medium text-muted-foreground">
                {label}
            </span>
            <Tooltip title={tooltip} />
        </div>
        {children}
    </div>
);

export const StateSummary = ({
    internalState,
    externalState,
    shopState,
    loading,
    enableLabel = 'Enable',
    onToggleState
}: StateSummaryProps) => {
    const isEnabled = internalState === ProductState.PRODUCTION;

    return (
        <div className="flex flex-wrap items-center gap-4">
            <StatePanel
                label="Internal State"
                tooltip="Admin controlled state. Use the button to enable/disable."
                highlighted
            >
                <div className="flex items-center gap-2">
                    <StateBadge state={internalState} empty="Not Set" />
                    {isEnabled ? (
                        <Button
                            variant="outline"
                            size="xs"
                            className="text-destructive"
                            loading={loading}
                            onClick={() => onToggleState(ProductState.DISABLED)}
                        >
                            <Ban />
                            Disable
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            size="xs"
                            className="text-success"
                            loading={loading}
                            onClick={() =>
                                onToggleState(ProductState.PRODUCTION)
                            }
                        >
                            <CircleCheck />
                            {enableLabel}
                        </Button>
                    )}
                </div>
            </StatePanel>

            <span className="text-lg text-muted-foreground">+</span>

            <StatePanel
                label="External State"
                tooltip="Read-only state from vendor source (e.g., Multivoucher, GoGift)."
            >
                <StateBadge state={externalState} empty="Not Available" />
            </StatePanel>

            <span className="text-lg text-muted-foreground">=</span>

            <StatePanel
                label="Shop State"
                tooltip="Final calculated state. PRODUCTION only when BOTH states are PRODUCTION."
            >
                <StateBadge state={shopState} empty="" />
            </StatePanel>
        </div>
    );
};
